// Tiny IndexedDB wrapper — no dependency, just enough for our needs.
// Stores: kv (single-key blobs), favorites, recents.

const DB_NAME = 'fyrefly';
// v2 (2026-05-22): added `track_active` store for the in-flight active
// track. Moves the unbounded 1-Hz GPS append off localStorage (which iOS
// evicts the entire origin when over quota) and into IDB which has a
// much higher per-origin budget. See audit-synthesis-2026-05-22.md CR-4.
// v3 (2026-05-24): added `track_index` (metadata-only rows for fast
// listing) and `track_logs` (completed point arrays keyed by track id).
// Moves finalized track payloads out of localStorage (5–10 MB iOS cap)
// into IDB (50+ MB per-origin budget) and eliminates the quota-silent-
// swallow data-loss path left by CR-4. See audit-synthesis-2026-05-24
// § CR-1.
const DB_VERSION = 3;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
      if (!db.objectStoreNames.contains('favorites')) {
        db.createObjectStore('favorites', { keyPath: 'ident' });
      }
      if (!db.objectStoreNames.contains('recents')) {
        db.createObjectStore('recents', { keyPath: 'ident' });
      }
      // v2: active track snapshot. Single-row store keyed by 'current'
      // (we only ever record one active track at a time). The value is
      // the full TrackLog including its `points` array. Keeping it in
      // IDB instead of localStorage prevents iOS Safari from evicting
      // the entire origin (auth, plans, waypoints, SW caches included)
      // when a 4-hour 1-Hz track approaches the localStorage budget.
      if (!db.objectStoreNames.contains('track_active')) {
        db.createObjectStore('track_active');
      }
      // v3: completed-track stores. track_index holds small metadata rows
      // (id, startedAt, endedAt, pointCount, distanceNm, notes) for fast
      // list rendering without loading 14,400-point arrays. track_logs
      // holds the raw TrackPoint[] arrays keyed by track id, loaded lazily
      // only when the detail / debrief / GPX-export path needs them.
      if (!db.objectStoreNames.contains('track_index')) {
        db.createObjectStore('track_index');
      }
      if (!db.objectStoreNames.contains('track_logs')) {
        db.createObjectStore('track_logs');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

// ── Active-track store helpers ────────────────────────────────────────────
//
// Generic typed wrappers around the `track_active` store. The track
// store treats this as an opaque blob keyed by the literal 'current'.
// We export typed wrappers so callers don't import this module's
// constants directly.
const ACTIVE_KEY = 'current';
export async function trackActiveGet<T = unknown>(): Promise<T | null> {
  try {
    const db = await openDb();
    return await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction('track_active', 'readonly');
      const req = tx.objectStore('track_active').get(ACTIVE_KEY);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}
export async function trackActiveSet<T = unknown>(value: T): Promise<void> {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction('track_active', 'readwrite');
    tx.objectStore('track_active').put(value, ACTIVE_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
export async function trackActiveClear(): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('track_active', 'readwrite');
      tx.objectStore('track_active').delete(ACTIVE_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch { /* ignore */ }
}

/** One-time cleanup: drop legacy mass-dataset blobs from IDB so they don't
 *  get warmed into memory on startup. Safe to call repeatedly; idempotent. */
export async function purgeLegacyDatasets(): Promise<void> {
  const legacy = [
    'airports.dataset.v1',
    'navaids.dataset.v1',
    'navaids.dataset.v2-nasr',
    'fixes.dataset.v1',
  ];
  try {
    const db = await openDb();
    await new Promise<void>((res, rej) => {
      const tx = db.transaction('kv', 'readwrite');
      const store = tx.objectStore('kv');
      for (const k of legacy) store.delete(k);
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
    });
  } catch { /* IDB unavailable; nothing to do */ }
}

export async function kvGet<T = unknown>(key: string): Promise<T | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('kv', 'readonly');
    const req = tx.objectStore('kv').get(key);
    req.onsuccess = () => resolve((req.result as T) ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function kvSet<T = unknown>(key: string, value: T): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('kv', 'readwrite');
    tx.objectStore('kv').put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function favPut(item: { ident: string; kind: 'airport' | 'navaid'; name: string; lat: number; lon: number; addedAt: number }) {
  const db = await openDb();
  return new Promise<void>((res, rej) => {
    const tx = db.transaction('favorites', 'readwrite');
    tx.objectStore('favorites').put(item);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

export async function favDelete(ident: string) {
  const db = await openDb();
  return new Promise<void>((res, rej) => {
    const tx = db.transaction('favorites', 'readwrite');
    tx.objectStore('favorites').delete(ident);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

export async function favList<T = any>(): Promise<T[]> {
  const db = await openDb();
  return new Promise((res, rej) => {
    const tx = db.transaction('favorites', 'readonly');
    const req = tx.objectStore('favorites').getAll();
    req.onsuccess = () => res(req.result as T[]);
    req.onerror = () => rej(req.error);
  });
}

export async function recentPut(item: { ident: string; kind: 'airport' | 'navaid'; name: string; lat: number; lon: number; lastSeenAt: number }) {
  const db = await openDb();
  return new Promise<void>((res, rej) => {
    const tx = db.transaction('recents', 'readwrite');
    tx.objectStore('recents').put(item);
    tx.oncomplete = () => res();
    tx.onerror = () => rej(tx.error);
  });
}

export async function recentList<T = any>(): Promise<T[]> {
  const db = await openDb();
  return new Promise((res, rej) => {
    const tx = db.transaction('recents', 'readonly');
    const req = tx.objectStore('recents').getAll();
    req.onsuccess = () => {
      const list = (req.result as T[]) ?? [];
      // Sort newest first
      list.sort((a: any, b: any) => (b.lastSeenAt ?? 0) - (a.lastSeenAt ?? 0));
      res(list);
    };
    req.onerror = () => rej(req.error);
  });
}

// ── Completed-track store helpers (v3) ──────────────────────────────────────
//
// track_index: keyed by track id, value is TrackMeta (no points).
//   Used by the list view and the store's `logs` array. Cheap to load
//   all metadata at startup: a 100-track index is ~20 KB vs. 150 MB
//   for 100 full-points arrays.
//
// track_logs: keyed by track id, value is TrackPoint[].
//   Loaded lazily only when the detail page, debrief, GPX export, or
//   server sync needs the raw fix data. A 4-hour 1-Hz track is ~1.5 MB
//   JSON; fine for IDB but would choke localStorage on busy iPhones.

/** Return all values from track_index, unsorted. Caller should sort by
 *  startedAt descending for list display. */
export async function trackIndexGetAll<T = unknown>(): Promise<T[]> {
  try {
    const db = await openDb();
    return new Promise<T[]>((resolve, reject) => {
      const tx = db.transaction('track_index', 'readonly');
      const req = tx.objectStore('track_index').getAll();
      req.onsuccess = () => resolve((req.result as T[]) ?? []);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

/** Write (upsert) a metadata row into track_index keyed by `id`. */
export async function trackIndexSet<T = unknown>(id: string, value: T): Promise<void> {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction('track_index', 'readwrite');
    tx.objectStore('track_index').put(value, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Delete a metadata row from track_index. Safe to call when the key
 *  doesn't exist (IDB delete is idempotent). */
export async function trackIndexDelete(id: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('track_index', 'readwrite');
      tx.objectStore('track_index').delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch { /* ignore */ }
}

/** Load the points array for `id` from track_logs. Returns null when
 *  the track has never been finalised to IDB (e.g. migration not run
 *  yet, or the track was deleted). */
export async function trackLogsGetPoints<T = unknown>(id: string): Promise<T | null> {
  try {
    const db = await openDb();
    return await new Promise<T | null>((resolve, reject) => {
      const tx = db.transaction('track_logs', 'readonly');
      const req = tx.objectStore('track_logs').get(id);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

/** Write (upsert) the points array for `id` into track_logs. This is
 *  the durable write in stop() — must complete before track_active is
 *  cleared. Throws on IDB quota or structure errors; caller must handle
 *  and preserve track_active for recovery if this throws. */
export async function trackLogsSetPoints<T = unknown>(id: string, points: T): Promise<void> {
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction('track_logs', 'readwrite');
    tx.objectStore('track_logs').put(points, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Delete the points array for `id` from track_logs. Called when the
 *  user removes a track (alongside trackIndexDelete). */
export async function trackLogsDeletePoints(id: string): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction('track_logs', 'readwrite');
      tx.objectStore('track_logs').delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch { /* ignore */ }
}
